/**
 * Theme Context for Stenope Academy
 * 
 * Manages dark/light theme mode across the application.
 */

import React, { createContext, useContext, useState, useMemo, useEffect } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

const ThemeModeContext = createContext();

export const useThemeMode = () => {
    const context = useContext(ThemeModeContext);
    if (!context) {
        throw new Error('useThemeMode must be used within a ThemeProvider');
    }
    return context;
};

export const ThemeProvider = ({ children }) => {
    // Initialize theme from localStorage or default to dark
    const [mode, setMode] = useState(() => {
        const saved = localStorage.getItem('themeMode');
        return saved === 'light' ? 'light' : 'dark';
    });

    // Save theme preference to localStorage
    useEffect(() => {
        localStorage.setItem('themeMode', mode);
    }, [mode]);

    const setThemeMode = (newMode) => {
        if (newMode === 'light' || newMode === 'dark') {
            setMode(newMode);
        }
    };

    const toggleTheme = () => {
        setMode((prev) => (prev === 'dark' ? 'light' : 'dark'));
    };

    // Create MUI theme
    const theme = useMemo(
        () =>
            createTheme({
                palette: {
                    mode,
                    primary: {
                        main: '#8B5CF6', // electric purple
                        light: '#A78BFA',
                        dark: '#7C3AED',
                    },
                    secondary: {
                        main: '#D946EF', // fuchsia neon
                        light: '#E879F9',
                        dark: '#C084FC',
                    },
                    background: {
                        default: mode === 'dark' ? '#03001C' : '#F8FAFC',
                        paper: mode === 'dark' ? 'rgba(15, 23, 42, 0.65)' : '#FFFFFF',
                    },
                    text: {
                        primary: mode === 'dark' ? '#F8FAFC' : '#0F172A',
                        secondary: mode === 'dark' ? '#94A3B8' : '#475569',
                    },
                    success: {
                        main: '#10B981', // emerald
                    },
                    error: {
                        main: '#EF4444', // red
                    },
                    warning: {
                        main: '#F59E0B', // amber
                    },
                },
                typography: {
                    fontFamily: '"Plus Jakarta Sans", "Outfit", "Inter", "Roboto", "Helvetica", sans-serif',
                    h1: {
                        fontWeight: 800,
                        fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                    },
                    h2: {
                        fontWeight: 800,
                        fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                    },
                    h3: {
                        fontWeight: 700,
                        fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                    },
                    h4: {
                        fontWeight: 700,
                        fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                    },
                    h5: {
                        fontWeight: 600,
                        fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                    },
                    h6: {
                        fontWeight: 600,
                        fontFamily: '"Outfit", "Plus Jakarta Sans", sans-serif',
                    },
                    button: {
                        textTransform: 'none',
                        fontWeight: 700,
                        fontFamily: '"Plus Jakarta Sans", sans-serif',
                    },
                },
                shape: {
                    borderRadius: 16,
                },
                components: {
                    MuiButton: {
                        styleOverrides: {
                            root: {
                                borderRadius: 12,
                                padding: '12px 28px',
                                transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                            },
                            contained: {
                                background: 'linear-gradient(135deg, #8B5CF6 0%, #D946EF 100%)',
                                boxShadow: '0 4px 14px rgba(139, 92, 246, 0.3)',
                                color: '#FFFFFF',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #7C3AED 0%, #C084FC 100%)',
                                    boxShadow: '0 6px 20px rgba(139, 92, 246, 0.5)',
                                    transform: 'translateY(-1px)',
                                },
                            },
                        },
                    },
                    MuiOutlinedInput: {
                        styleOverrides: {
                            root: {
                                borderRadius: 12,
                                transition: 'all 0.3s ease',
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#8B5CF6',
                                    boxShadow: '0 0 10px rgba(139, 92, 246, 0.2)',
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'rgba(139, 92, 246, 0.5)',
                                },
                            },
                        },
                    },
                    MuiTextField: {
                        styleOverrides: {
                            root: {
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 12,
                                },
                            },
                        },
                    },
                    MuiPaper: {
                        styleOverrides: {
                            root: {
                                backgroundImage: 'none',
                                backdropFilter: 'blur(16px)',
                            },
                        },
                    },
                },
            }),
        [mode]
    );

    const value = {
        mode,
        setThemeMode,
        toggleTheme,
        isDark: mode === 'dark',
    };

    return (
        <ThemeModeContext.Provider value={value}>
            <MuiThemeProvider theme={theme}>
                <CssBaseline />
                {children}
            </MuiThemeProvider>
        </ThemeModeContext.Provider>
    );
};

export default ThemeProvider;
